import type { Vehicle } from "./types";

export interface PriceTier {
  label: string;
  field: keyof Pick<
    Vehicle,
    | "price_1d"
    | "price_2_3d"
    | "price_4_7d"
    | "price_8_14d"
    | "price_15_29d"
    | "price_30_179d"
    | "price_180_364d"
    | "price_365_999d"
  >;
  min: number;
  max: number;
}

// Ascending by `min` — VehicleCard "nuo X €" still computes via Math.min
// across all 8 prices to be robust to admin entry mistakes.
export const TIERS: PriceTier[] = [
  { label: "1 para",         field: "price_1d",        min: 1,   max: 1 },
  { label: "2-3 paros",      field: "price_2_3d",      min: 2,   max: 3 },
  { label: "4-7 paros",      field: "price_4_7d",      min: 4,   max: 7 },
  { label: "8-14 paros",     field: "price_8_14d",     min: 8,   max: 14 },
  { label: "15-29 paros",    field: "price_15_29d",    min: 15,  max: 29 },
  { label: "30-179 paros",   field: "price_30_179d",   min: 30,  max: 179 },
  { label: "180-364 paros",  field: "price_180_364d",  min: 180, max: 364 },
  { label: "365-999 paros",  field: "price_365_999d",  min: 365, max: 999 },
];

export function getTier(days: number): PriceTier {
  for (const tier of TIERS) {
    if (days >= tier.min && days <= tier.max) return tier;
  }
  // out-of-range — clamp to nearest end. Validation should prevent this,
  // but defensive: <1 → first tier, >999 → last tier.
  return days < TIERS[0].min ? TIERS[0] : TIERS[TIERS.length - 1];
}

export interface PriceCalculation {
  tier: PriceTier;
  pricePerDay: number;
  totalPrice: number;
  days: number;
}

export function calculatePrice(days: number, vehicle: Vehicle): PriceCalculation {
  const tier = getTier(days);
  const pricePerDay = Number(vehicle[tier.field]);
  return {
    tier,
    pricePerDay,
    totalPrice: pricePerDay * days,
    days,
  };
}

export function getMinDailyPrice(vehicle: Vehicle): number {
  return Math.min(
    ...TIERS.map((t) => Number(vehicle[t.field])),
  );
}

/**
 * Compute billable days from datetime range using ceiling of hours/24.
 * 24h = 1 day, 24h01m = 2 days, 25h = 2 days.
 *
 * @param dateFrom "YYYY-MM-DD"
 * @param dateTo "YYYY-MM-DD"
 * @param pickupTime "HH:MM" or "HH:MM:SS"
 * @param returnTime "HH:MM" or "HH:MM:SS"
 */
export function computeBillableDays(
  dateFrom: string,
  dateTo: string,
  pickupTime: string,
  returnTime: string,
): number {
  const pickup = new Date(`${dateFrom}T${normalizeTime(pickupTime)}`);
  const ret = new Date(`${dateTo}T${normalizeTime(returnTime)}`);
  const hours = (ret.getTime() - pickup.getTime()) / (1000 * 60 * 60);
  if (!isFinite(hours) || hours <= 0) return 1;
  return Math.max(1, Math.ceil(hours / 24));
}

function normalizeTime(t: string): string {
  // Accept "HH:MM" or "HH:MM:SS" — always pass "HH:MM:SS" to Date().
  return t.length === 5 ? `${t}:00` : t;
}
